import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Bot,
  Building2,
  CheckCircle2,
  CirclePlay,
  FileCheck2,
  Link2,
  LockKeyhole,
  Mail,
  MessageSquare,
  Network,
  PhoneCall,
  Rocket,
  Send,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  Users,
  Volume2,
  VolumeX,
  Workflow,
  Wrench,
  Zap,
  Star,
} from 'lucide-react';
import { Section, fadeInUp } from '../components/Section';
import SEO from '../components/SEO';
import HeroCore3D from '../components/HeroCore3D';
import Newsletter from '../components/Newsletter';
import { trackLeadEvent } from '../lib/tracking';
import { SOCIAL_URLS } from '../lib/socialLinks';

const HERO_VISUAL = 'https://static.prod-images.emergentagent.com/jobs/cf4b6ddd-cd8a-43a0-80c0-50a20361cba4/images/421c593f7519aa09d684f3939efecb82e6d414d6c252afeabb8271b5e0562e81.jpeg';
const VALUE_VIDEO = '/demo-videos/intro-neuralia369.mp4';

const HERO_BADGES = [
  'Aynara como capa orquestadora',
  'Contratos + Auth Links',
  'Conexiones embebidas seguras',
  'Checkout LIVE',
];

const SOLUTION_PILLARS = [
  {
    id: 'ventas',
    icon: Rocket,
    title: 'Ventas y onboarding real',
    copy: 'Contrato, checkout, usuario, contraseña y activación del cliente sin hojas sueltas ni caos comercial.',
  },
  {
    id: 'orquestacion',
    icon: Workflow,
    title: 'Orquestación de herramientas',
    copy: 'Gmail, CRM, documentos, calendario, WhatsApp y agentes conectados desde una sola capa de operación.',
  },
  {
    id: 'seguridad',
    icon: ShieldCheck,
    title: 'Control, trazabilidad y permisos',
    copy: 'Roles, accesos, consentimiento por Auth Link y control sobre quién ve, ejecuta y aprueba cada acción.',
  },
  {
    id: 'equipos',
    icon: Building2,
    title: 'Equipos multirrol y operación compartida',
    copy: 'Dirección, ventas, operaciones y marketing con vistas adaptadas a cada función real.',
  },
];

const WHAT_WE_ARE = [
  'Un sistema operativo conversacional para vender, coordinar, atender y ejecutar.',
  'Una capa de orquestación que conecta las herramientas existentes con agentes y automatización.',
  'Una infraestructura para contratos, Auth Links, onboarding y operación real sin fricción.',
  'Una forma de introducir IA sobre el negocio real, no aparte del negocio.',
];

const WHAT_WE_ARE_NOT = [
  'No es una app aislada que obliga a rehacer tu operación.',
  'No es una consultoría sin entrega ni sin activación real.',
  'No es una colección de herramientas sueltas sin contexto.',
  'No es automatización ciega: hay control, permisos y supervisión.',
];

const COMPARISON_ROWS = [
  {
    label: 'Tiempo hasta valor',
    fragmented: 'Meses combinando apps, pruebas y errores.',
    neuralia: 'Ruta clara: contrato, conexión, activación y primer valor útil en días.',
  },
  {
    label: 'Datos y contexto',
    fragmented: 'Información dispersa, duplicada y difícil de gobernar.',
    neuralia: 'Memoria operativa, documentos, CRM y canales conectados en un mismo flujo.',
  },
  {
    label: 'Seguridad y consentimiento',
    fragmented: 'Credenciales compartidas, accesos poco claros y riesgos operativos.',
    neuralia: 'Auth Links, contratos, permisos por rol y trazabilidad desde el diseño.',
  },
  {
    label: 'Escalado comercial',
    fragmented: 'Cada nueva venta añade más complejidad manual.',
    neuralia: 'Cada venta puede disparar onboarding, comunicaciones y paneles ya preparados.',
  },
  {
    label: 'Adopción del equipo',
    fragmented: 'Formación genérica y herramientas que nadie acaba usando bien.',
    neuralia: 'Experiencias más simples: una capa conversacional y paneles según función.',
  },
  {
    label: 'Continuidad',
    fragmented: 'Todo depende de recordar pasos y personas clave.',
    neuralia: 'El sistema guarda estructura, memoria y siguiente paso operativo.',
  },
];

const CONNECTORS = [
  { name: 'Gmail', logo: 'https://logos.composio.dev/api/gmail' },
  { name: 'HubSpot', logo: 'https://logos.composio.dev/api/hubspot' },
  { name: 'Notion', logo: 'https://logos.composio.dev/api/notion' },
  { name: 'Google Drive', logo: 'https://logos.composio.dev/api/googledrive' },
  { name: 'Slack', logo: 'https://logos.composio.dev/api/slack' },
  { name: 'Google Calendar', logo: 'https://logos.composio.dev/api/googlecalendar' },
  { name: 'Outlook', logo: 'https://logos.composio.dev/api/outlook' },
  { name: 'Google Sheets', logo: 'https://logos.composio.dev/api/googlesheets' },
];

const CONNECTION_FLOW = [
  {
    id: 'contract',
    icon: FileCheck2,
    title: 'Contrato y aceptación',
    copy: 'Formalizas el acuerdo antes de tocar datos sensibles ni pedir accesos improvisados.',
  },
  {
    id: 'auth-link',
    icon: Link2,
    title: 'Auth Link seguro',
    copy: 'El cliente autoriza conexiones sin compartir contraseñas directamente contigo.',
  },
  {
    id: 'embedded',
    icon: LockKeyhole,
    title: 'Conexiones embebidas',
    copy: 'Aynara y los agentes usan Gmail, CRM y herramientas autorizadas desde un hub gobernado.',
  },
];

const TEAM_ROLES = [
  {
    id: 'direccion',
    label: 'Dirección',
    title: 'Visión global del negocio',
    copy: 'Controla métricas, contratos, cobros, prioridades y estado real de la operación sin perder contexto.',
    bullets: ['Panel ejecutivo', 'Prioridades de negocio', 'Cobros y contratos', 'Visión comercial y operativa'],
    metrics: ['Pipeline activo', 'Cobros / contratos', 'Conexiones autorizadas'],
  },
  {
    id: 'operaciones',
    label: 'Operaciones',
    title: 'Coordinación y seguimiento diario',
    copy: 'Gestiona agenda, documentos, pendientes, recordatorios y continuidad interna desde una sola capa.',
    bullets: ['Agenda y recordatorios', 'Documentación pendiente', 'Tareas operativas', 'Seguimientos del día'],
    metrics: ['Pendientes críticos', 'Seguimientos abiertos', 'Procesos en curso'],
  },
  {
    id: 'ventas',
    label: 'Ventas',
    title: 'Pipeline, propuestas y continuidad comercial',
    copy: 'Accede a leads, estado de propuestas, prioridades y siguiente acción sin depender de memoria manual.',
    bullets: ['Leads y propuestas', 'Siguiente acción', 'Seguimiento comercial', 'Estado de cierre'],
    metrics: ['Leads activos', 'Propuestas abiertas', 'Oportunidades calientes'],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    title: 'Campañas, mensajes y reputación',
    copy: 'Coordina contenido, campañas, respuestas y piezas con una lectura más clara del negocio y sus prioridades.',
    bullets: ['Campañas activas', 'Creatividades', 'Mensajes entrantes', 'Prueba social y reputación'],
    metrics: ['Campañas', 'Mensajes', 'Contenido listo'],
  },
];

const LIVE_PROOF = [
  {
    id: 'checkout',
    title: 'Cobros reales funcionando',
    copy: 'Checkout LIVE, suscripciones activas y reconciliación segura cuando Stripe cobra antes que Mongo registre.',
  },
  {
    id: 'contracts',
    title: 'Contratos premium listos',
    copy: 'PDF corporativo aprobado y reutilizable para nuevas ventas y nuevas relaciones comerciales.',
  },
  {
    id: 'connections',
    title: 'Conexiones autorizadas',
    copy: 'Gmail real conectado, Auth Links activos y arquitectura preparada para seguir integrando herramientas.',
  },
  {
    id: 'onboarding',
    title: 'Onboarding partner validado',
    copy: 'Usuario, contraseña temporal, panel partner y flujo de alta comprobados en entorno real/controlado.',
  },
];

const REAL_CASES = [
  {
    id: 'sale',
    label: 'Venta real + contrato',
    title: 'Cobro real validado y contrato premium reutilizable',
    copy: 'La web ya puede sostener una venta que termina en contrato, checkout, validación de pago y siguiente paso claro.',
    tags: ['Stripe LIVE', 'Contrato premium', 'Reconciliación segura'],
  },
  {
    id: 'connections',
    label: 'Conexión real',
    title: 'Gmail conectado y Auth Links listos para autorizar más herramientas',
    copy: 'Ya no hablamos de integraciones futuras en abstracto: el sistema ya usa una conexión real y una lógica de consentimiento operativa.',
    tags: ['Nango Hub', 'Gmail', 'Auth Link'],
  },
  {
    id: 'partner',
    label: 'Caso partner',
    title: 'Onboarding partner probado con acceso, contraseña y panel',
    copy: 'El alta comercial ya se convirtió en una experiencia real: usuario, login, plan, panel y continuidad operativa.',
    tags: ['Programa partner', 'Panel partner', 'Onboarding'],
  },
  {
    id: 'teams',
    label: 'Caso multi-equipo',
    title: 'Base multirrol preparada para empresas que necesitan orden real',
    copy: 'Mostramos una arquitectura creíble para dirección, operaciones, ventas y marketing sin caer en promesas genéricas.',
    tags: ['Dirección', 'Operaciones', 'Ventas', 'Marketing'],
  },
];

const ROI_OUTCOMES = [
  {
    id: 'orden',
    day: 'Día 1-2',
    title: 'Orden y claridad operativa',
    copy: 'Aterrizamos qué se vende, qué se conecta, qué se automatiza primero y qué no debe seguir disperso.',
  },
  {
    id: 'conexiones',
    day: 'Día 2-4',
    title: 'Primeras conexiones activas',
    copy: 'Dejamos preparada o activada la primera capa útil: contrato, Auth Link, Gmail, CRM o herramienta crítica del negocio.',
  },
  {
    id: 'flujo',
    day: 'Día 4-7',
    title: 'Primer flujo comercial funcionando',
    copy: 'La venta deja de depender de improvisación: contrato, acceso, onboarding y siguiente paso quedan más cerca de correr como sistema.',
  },
];

const PENDING_CONNECTIONS = [
  {
    id: 'meta',
    priority: 'P1',
    title: 'Meta WhatsApp Cloud API',
    copy: 'La prioridad comercial más clara para activar comunicaciones reales, seguimiento y automatización sobre WhatsApp.',
  },
  {
    id: 'hubspot',
    priority: 'P2',
    title: 'HubSpot OAuth',
    copy: 'Clave para reforzar CRM, captación, seguimiento y visibilidad del pipeline comercial dentro del sistema.',
  },
  {
    id: 'outlook',
    priority: 'P2',
    title: 'Outlook / Microsoft OAuth',
    copy: 'Importante para empresas que viven en Microsoft 365 y necesitan correo, calendario y coordinación en ese stack.',
  },
];

const PROCESS_STEPS = [
  {
    id: 'diagnostico',
    title: '1. Diagnóstico y oferta clara',
    copy: 'Definimos qué hay que conectar, qué hay que vender y qué parte del negocio debe automatizarse primero.',
  },
  {
    id: 'formalizacion',
    title: '2. Contrato + checkout + consentimiento',
    copy: 'Formalizas la relación y activas el acceso sin perseguir documentos, pagos ni autorizaciones por separado.',
  },
  {
    id: 'conexion',
    title: '3. Conexión embebida y configuración',
    copy: 'Aynara y el sistema conectan herramientas autorizadas y preparan el entorno operativo del cliente.',
  },
  {
    id: 'operacion',
    title: '4. Operación, seguimiento y expansión',
    copy: 'Ya no solo vendes una entrega: activas una operación continua con paneles, tareas y siguiente paso definido.',
  },
];

const VALUE_VIDEO_POINTS = [
  'Qué problema resolvemos: dispersión, pérdida de tiempo y falta de memoria operativa.',
  'Qué hace Aynara: coordina agentes, herramientas, contratos, datos y comunicaciones.',
  'Qué puede comprar un cliente: activación real, onboarding, paneles y automatización útil para empresas, equipos y partners.',
  'Qué cambia comercialmente: más control, más velocidad y menos fricción en cada venta.',
];

const AynaraVoiceDemo = () => {
  const [state, setState] = useState('idle');
  const audioRef = useRef(null);

  const playDemo = async () => {
    if (state === 'playing' && audioRef.current) {
      audioRef.current.pause();
      setState('idle');
      return;
    }
    setState('loading');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: 'Hola, soy Aynara, la inteligencia de Neuralia, tres, seis, nueve. Conecto tus herramientas, automatizo tus procesos y dirijo tu negocio contigo. Y solo pagas por lo que consumes. ¿Empezamos?' }),
      });
      if (!res.ok) throw new Error('tts');
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setState('idle');
      await audio.play();
      setState('playing');
      trackLeadEvent('aynara_voice_demo');
    } catch (err) {
      setState('idle');
    }
  };

  return (
    <button
      type="button"
      onClick={playDemo}
      disabled={state === 'loading'}
      className="btn-secondary w-full sm:w-auto justify-center border-fuchsia-400/40 text-fuchsia-300 hover:bg-fuchsia-400/10 disabled:opacity-60"
      data-testid="homepage-hero-voice-demo-btn"
    >
      {state === 'playing' ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      {state === 'loading' ? 'Generando voz…' : state === 'playing' ? 'Detener voz' : 'Escuchar a Aynara'}
    </button>
  );
};

const HeroSection = () => (
  <section
    id="hero"
    className="min-h-screen flex items-center relative overflow-hidden pt-24 pb-12 bg-[radial-gradient(circle_at_15%_20%,rgba(0,229,255,0.16),transparent_25%),radial-gradient(circle_at_82%_24%,rgba(255,255,255,0.07),transparent_18%),linear-gradient(180deg,#030712_0%,#081226_58%,#020617_100%)]"
    data-testid="homepage-hero-section"
  >
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute top-[10%] left-[5%] h-[20rem] w-[20rem] rounded-full bg-cyan-400/10 blur-[110px]" />
      <div className="absolute bottom-[8%] right-[8%] h-[28rem] w-[28rem] rounded-full bg-white/5 blur-[120px]" />
      <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:72px_72px]" />
    </div>

    <div className="max-w-7xl mx-auto px-golden-lg w-full relative z-10">
      <div className="grid gap-golden-xl lg:grid-cols-[1.06fr_0.94fr] items-start">
        <motion.div initial={{ opacity: 0, x: -26 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
          <p className="editorial-kicker mb-5 text-slate-200/85" data-testid="homepage-hero-kicker">
            Neuralia 369 OS · Orquestación empresarial con Aynara
          </p>
          <h1 className="max-w-5xl text-white leading-[0.94] mb-golden-lg" data-testid="homepage-hero-title">
            Orquestación empresarial con agentes supervisados. Conecta, cobra, opera y crece con{' '}
            <span className="inline-block pr-1 bg-[linear-gradient(90deg,#4285F4_0%,#EA4335_33%,#FBBC05_66%,#34A853_100%)] bg-clip-text text-transparent">control humano</span>{' '}
            en cada decisión.
          </h1>
          <p className="max-w-3xl text-xl text-slate-100/90 mb-4" data-testid="homepage-hero-subtitle">
            Neuralia 369 OS une contratos, Auth Links, conectores, agentes y paneles en una sola capa operativa. Aynara decide qué activar, qué herramienta usar y cuándo pedir aprobación.
          </p>
          <div className="mb-4 rounded-2xl border border-cyan-300/30 bg-cyan-400/10 px-5 py-4" data-testid="homepage-hero-fair-billing">
            <p className="text-base sm:text-lg font-bold text-white">
              La única plataforma de IA con facturación justa y real. <span className="text-cyan-300">Pagas por lo que consumes.</span>
            </p>
            <Link to="/precios" className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-cyan-200 hover:text-white transition-colors" data-testid="homepage-hero-pricing-cta">
              Ver precios y calculadora de consumo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-3 mb-golden-lg" data-testid="homepage-hero-badges">
            {HERO_BADGES.map((item) => (
              <span key={item} className="insight-chip border-white/10 bg-white/5 text-slate-100">
                {item}
              </span>
            ))}
          </div>

          <div className="flex flex-wrap gap-golden-md" data-testid="homepage-hero-cta-group">
            <Link to="/experiencia-3d" className="btn-primary w-full sm:w-auto justify-center" data-testid="homepage-hero-primary-cta">
              Ver demo de Aynara
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="/seguridad-agentes" className="btn-secondary w-full sm:w-auto justify-center border-white/15 text-slate-100 hover:bg-white/5" data-testid="homepage-hero-secondary-cta">
              Descargar guía de seguridad para agentes
            </a>
            <a href="https://wa.me/17872981758" target="_blank" rel="noopener noreferrer" className="btn-secondary w-full sm:w-auto justify-center border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10" data-testid="homepage-hero-whatsapp-cta">
              <MessageSquare className="w-4 h-4" /> WhatsApp
            </a>
            <AynaraVoiceDemo />
          </div>

          <div className="mt-golden-xl grid gap-4 md:grid-cols-3">
            <div className="glass-card tracing-panel px-5 py-5 bg-white/5 border-white/10" data-testid="homepage-hero-card-ventas">
              <p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Ventas</p>
              <p className="mt-2 text-base text-slate-100">Checkout, contrato, onboarding y paneles conectados a una venta real.</p>
            </div>
            <div className="glass-card tracing-panel px-5 py-5 bg-white/5 border-white/10" data-testid="homepage-hero-card-conexiones">
              <p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Conexiones</p>
              <p className="mt-2 text-base text-slate-100">OAuth embebido, Auth Links y acceso seguro a herramientas del cliente.</p>
            </div>
            <div className="glass-card tracing-panel px-5 py-5 bg-white/5 border-white/10" data-testid="homepage-hero-card-operacion">
              <p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Operación</p>
              <p className="mt-2 text-base text-slate-100">Dirección, partners y equipos trabajando con una misma memoria operativa.</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative">
          <div className="editorial-image-shell relative min-h-[28rem] overflow-hidden border-white/10 bg-[#02040a]" data-testid="homepage-hero-visual-shell">
            <HeroCore3D className="opacity-95" />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(2,6,23,0.92)] via-transparent to-transparent" />
            <div className="absolute left-5 right-5 top-5 flex flex-wrap gap-2" data-testid="homepage-hero-live-signals">
              <span className="rounded-full border border-cyan-300/30 bg-cyan-300/12 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-cyan-200">LIVE</span>
              <span className="rounded-full border border-white/10 bg-white/10 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-white/80">Stripe</span>
              <span className="rounded-full border border-white/10 bg-white/10 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-white/80">Auth Links</span>
              <span className="rounded-full border border-white/10 bg-white/10 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-white/80">Nango Hub</span>
            </div>
            <div className="absolute left-5 right-5 bottom-5 grid gap-3">
              <div className="glass-card tracing-panel border-white/10 bg-slate-950/60 p-5" data-testid="homepage-hero-floating-card-ayna">
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Aynara</p>
                <p className="mt-2 text-lg font-semibold text-white">La capa que conecta tu operación con la acción correcta.</p>
                <p className="mt-2 text-sm text-slate-200/82">Decide si toca conectar, cobrar, responder, activar un agente o escalar un flujo completo.</p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="glass-card tracing-panel border-white/10 bg-white/5 p-4" data-testid="homepage-hero-floating-card-authlink">
                  <p className="text-sm font-semibold text-white">Contrato + Auth Link</p>
                  <p className="mt-1 text-xs text-slate-300">Formaliza y conecta sin compartir contraseñas.</p>
                </div>
                <div className="glass-card tracing-panel border-white/10 bg-white/5 p-4" data-testid="homepage-hero-floating-card-panels">
                  <p className="text-sm font-semibold text-white">Paneles por rol</p>
                  <p className="mt-1 text-xs text-slate-300">Dirección, operaciones y partners trabajando sin dispersión.</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);


const LiveSignalsBar = () => {
  const signals = [
    'Comandos de voz operativos',
    'MCP activo',
    <a href="/integraciones/deepseek" className="hover:text-white transition-colors">DeepSeek integrado</a>,
    'Auth Links funcionando',
    'Stripe LIVE',
  ];
  return (
    <div className="w-full border-y border-white/8 bg-[#050d1a]/80 backdrop-blur-sm overflow-hidden" data-testid="homepage-live-signals-bar">
      <div className="relative flex items-center py-2.5">
        {/* Left glow mask */}
        <div className="absolute left-0 w-16 h-full bg-gradient-to-r from-[#050d1a] to-transparent z-10 pointer-events-none" />
        {/* Right glow mask */}
        <div className="absolute right-0 w-16 h-full bg-gradient-to-l from-[#050d1a] to-transparent z-10 pointer-events-none" />
        {/* Scrolling strip */}
        <motion.div
          className="flex items-center gap-8 whitespace-nowrap pl-8"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ duration: 28, ease: 'linear', repeat: Infinity }}
        >
          {[...signals, ...signals].map((s, i) => (
            <span key={i} className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.15em] uppercase text-slate-300">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_6px_rgba(0,229,255,0.8)]" />
              {s}
            </span>
          ))}
        </motion.div>
      </div>
    </div>
  );
};


/* ─────────────────────────────────────────────────────────────────────────
   EXTRACTO 1 — SISTEMA MULTIAGENTE
───────────────────────────────────────────────────────────────────────── */
const AGENTS_MINI = [
  { label: 'Ventas',  icon: Rocket,    color: '#3B82F6' },
  { label: 'Agenda',  icon: CheckCircle2, color: '#10B981' },
  { label: 'CRM',     icon: Users,     color: '#8B5CF6' },
  { label: 'Cobros',  icon: Zap,       color: '#F59E0B' },
  { label: 'Aynara',  icon: Bot,       color: '#00E5FF', hub: true },
];

const MULTIAGENT_BULLETS = [
  'Cada agente tiene su rol, herramientas autorizadas y punto de control humano.',
  'Aynara orquesta quién actúa, cuándo y con qué permisos en tiempo real.',
  'El sistema opera con autonomía donde puede, con supervisión donde debe.',
];

const ExtractMultiagente = () => (
  <section className="py-16 sm:py-20 border-b border-white/6 bg-[#030b18]" data-testid="extract-multiagente">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">

        {/* Left — copy */}
        <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
          <p className="editorial-kicker mb-4 text-cyan-300/90">Sistema Multiagente</p>
          <h2 className="text-white font-heading text-3xl sm:text-4xl font-black leading-tight mb-5">
            No un agente, <span className="gradient-text">un equipo supervisado</span>
          </h2>
          <ul className="space-y-3 mb-8">
            {MULTIAGENT_BULLETS.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-300 text-sm leading-relaxed">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                {b}
              </li>
            ))}
          </ul>
          <Link to="/sistema-multiagente" className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-white transition-colors group" data-testid="extract-multiagente-cta">
            Ver diagrama completo del ecosistema <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {/* Right — mini diagram */}
        <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
          <div className="relative rounded-2xl border border-white/8 bg-white/[0.03] p-8 overflow-hidden" data-testid="extract-multiagente-diagram">
            {/* Background glow */}
            <div className="absolute inset-0 rounded-2xl bg-[radial-gradient(circle_at_50%_50%,rgba(0,229,255,0.06),transparent_65%)] pointer-events-none" />

            {/* Hub center */}
            <div className="flex flex-col items-center mb-6">
              <div className="w-14 h-14 rounded-full bg-cyan-400/15 border border-cyan-400/35 flex items-center justify-center shadow-[0_0_24px_rgba(0,229,255,0.25)]">
                <Bot className="w-7 h-7 text-cyan-300" />
              </div>
              <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-cyan-300">Aynara — Orquestador</p>
            </div>

            {/* Agent nodes */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Ventas',  Icon: Rocket,       color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/25' },
                { label: 'Agenda',  Icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/25' },
                { label: 'CRM',     Icon: Users,        color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/25' },
                { label: 'Cobros',  Icon: Zap,          color: 'text-amber-400',  bg: 'bg-amber-500/10',  border: 'border-amber-500/25' },
              ].map(({ label, Icon, color, bg, border }) => (
                <div key={label} className={`rounded-xl border ${border} ${bg} p-3 flex flex-col items-center gap-1.5`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                  <p className={`text-[10px] font-bold uppercase tracking-wider ${color}`}>{label}</p>
                </div>
              ))}
            </div>

            {/* Connector line */}
            <div className="absolute top-[82px] left-1/2 -translate-x-1/2 w-px h-8 bg-gradient-to-b from-cyan-400/40 to-transparent" />

            {/* Bottom caption */}
            <p className="mt-5 text-center text-[11px] text-slate-500 tracking-wide">
              Cada agente actúa dentro de sus permisos · Control humano en cada decisión crítica
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────────
   EXTRACTO 2 — SEGURIDAD
───────────────────────────────────────────────────────────────────────── */
const SECURITY_BULLETS = [
  { title: 'Human-in-the-loop por diseño', desc: 'Ningún agente ejecuta acciones irreversibles sin aprobación humana explícita.' },
  { title: 'Auth Links como consentimiento', desc: 'Cada herramienta conectada requiere un Auth Link específico y revocable en un clic.' },
  { title: 'Permisos mínimos por agente y por rol', desc: 'Cada agente accede solo a lo que necesita. Sin herencias ocultas ni privilegios excesivos.' },
];

const ExtractSeguridad = () => (
  <section className="py-16 sm:py-20 border-b border-white/6 bg-[#02060f]" data-testid="extract-seguridad">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">

        {/* Left — stat + bullets */}
        <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
          <p className="editorial-kicker mb-4 text-red-400/90">Seguridad de agentes</p>
          <h2 className="text-white font-heading text-3xl sm:text-4xl font-black leading-tight mb-5">
            Control humano en <span className="text-red-400">cada decisión crítica</span>
          </h2>

          {/* Stat destacado */}
          <div className="mb-6 rounded-2xl border border-red-400/25 bg-red-400/8 px-5 py-4" data-testid="extract-seguridad-stat">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-400/80 mb-1">Dato verificado · CrowdStrike 2025</p>
            <p className="text-base sm:text-lg font-black text-white leading-snug">
              "El tiempo medio de acceso a infraestructuras empresariales cayó a{' '}
              <span className="text-red-400">29 minutos</span> en 2025."
            </p>
          </div>

          <ul className="space-y-3 mb-8">
            {SECURITY_BULLETS.map(({ title, desc }) => (
              <li key={title} className="flex items-start gap-3">
                <ShieldCheck className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-white">{title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
                </div>
              </li>
            ))}
          </ul>
          <Link to="/seguridad-agentes" className="inline-flex items-center gap-2 text-sm font-semibold text-red-400 hover:text-white transition-colors group" data-testid="extract-seguridad-cta">
            Ver los 6 pilares de seguridad <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {/* Right — visual pillar grid */}
        <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
          <div className="grid grid-cols-2 gap-3" data-testid="extract-seguridad-pillars">
            {[
              { n: '01', label: 'Human-in-the-loop', color: 'border-red-400/30 bg-red-400/5' },
              { n: '02', label: 'Auth Links', color: 'border-orange-400/30 bg-orange-400/5' },
              { n: '03', label: 'Permisos mínimos', color: 'border-amber-400/30 bg-amber-400/5' },
              { n: '04', label: 'Aislamiento de contexto', color: 'border-yellow-400/30 bg-yellow-400/5' },
              { n: '05', label: 'Auditoría completa', color: 'border-lime-400/30 bg-lime-400/5' },
              { n: '06', label: 'Revocación instantánea', color: 'border-emerald-400/30 bg-emerald-400/5' },
            ].map(({ n, label, color }) => (
              <div key={n} className={`rounded-xl border ${color} p-4 flex items-start gap-3`}>
                <span className="text-[10px] font-black text-slate-500 leading-none pt-0.5">{n}</span>
                <p className="text-xs font-semibold text-slate-300 leading-snug">{label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────────
   EXTRACTO 3 — SOLUCIONES POR SECTOR
───────────────────────────────────────────────────────────────────────── */
const SOLUTION_CARDS = [
  {
    id: 'ecommerce',
    Icon: ShoppingCart,
    label: 'Para E-commerce',
    color: 'text-cyan-400',
    border: 'border-cyan-400/25',
    bg: 'bg-cyan-400/5',
    glow: 'rgba(0,229,255,0.12)',
    bullets: [
      'Agente de cobros con Stripe integrado',
      'CRM automático de compradores y leads',
      'Notificaciones WA para carritos abandonados',
    ],
  },
  {
    id: 'admin-publica',
    Icon: Building2,
    label: 'Para Administración Pública',
    color: 'text-violet-400',
    border: 'border-violet-400/25',
    bg: 'bg-violet-400/5',
    glow: 'rgba(139,92,246,0.12)',
    bullets: [
      'Flujos de aprobación con Human-in-the-loop',
      'Auditoría completa de cada acción del agente',
      'Consentimiento explícito via Auth Links',
    ],
  },
  {
    id: 'agencias',
    Icon: Users,
    label: 'Para Agencias y Partners',
    color: 'text-emerald-400',
    border: 'border-emerald-400/25',
    bg: 'bg-emerald-400/5',
    glow: 'rgba(16,185,129,0.12)',
    bullets: [
      'Panel partner con métricas y pipeline propio',
      'Onboarding de clientes en menos de 10 minutos',
      'Agente de ventas con scoring automático',
    ],
  },
];

const ExtractSoluciones = () => (
  <section className="py-16 sm:py-20 border-b border-white/6 bg-[#030a17]" data-testid="extract-soluciones">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-10">
        <p className="editorial-kicker mb-3 text-slate-300/80">Soluciones por sector</p>
        <h2 className="text-white font-heading text-3xl sm:text-4xl font-black leading-tight">
          El mismo sistema, <span className="gradient-text">adaptado a tu operación</span>
        </h2>
      </motion.div>

      <div className="grid sm:grid-cols-3 gap-5 mb-8">
        {SOLUTION_CARDS.map(({ id, Icon, label, color, border, bg, glow, bullets }, idx) => (
          <motion.div
            key={id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: idx * 0.1 }}
            className={`relative rounded-2xl border ${border} ${bg} p-6 overflow-hidden group`}
            data-testid={`extract-solution-card-${id}`}
          >
            {/* Glow */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
              style={{ background: `radial-gradient(circle at 50% 0%, ${glow}, transparent 70%)` }} />

            <div className={`w-10 h-10 rounded-xl ${bg} border ${border} flex items-center justify-center mb-4`}>
              <Icon className={`w-5 h-5 ${color}`} />
            </div>
            <p className={`text-sm font-black uppercase tracking-wider ${color} mb-3`}>{label}</p>
            <ul className="space-y-2 mb-5">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-xs text-slate-300 leading-relaxed">
                  <span className={`w-1 h-1 rounded-full ${color.replace('text-', 'bg-')} flex-shrink-0 mt-1.5`} />
                  {b}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <div className="text-center">
        <Link to="/soluciones" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white border border-white/15 hover:border-white/30 rounded-full px-6 py-2.5 transition-all group" data-testid="extract-soluciones-cta">
          Ver todas las soluciones <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  </section>
);

const UspBanner = () => (
  <section className="mx-auto mt-2 max-w-7xl px-4 sm:px-6 lg:px-8" data-testid="homepage-usp-banner">
    <div className="overflow-hidden rounded-[24px] border border-cyan-300/25 bg-[radial-gradient(120%_140%_at_10%_0%,rgba(0,212,255,0.14),rgba(2,6,23,0.96)_55%)] p-6 sm:p-9">
      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-300">Únicos en el mercado</p>
      <h2 className="mt-3 text-base sm:text-lg font-black text-white">La única plataforma de IA que cobra por CONSUMO REAL de créditos y uso del sistema.</h2>
      <p className="mt-3 max-w-3xl text-sm sm:text-base text-slate-300">Conectamos todas las IA y herramientas en un solo idioma, con facturación real del consumo generado por créditos y uso de Neuralia369 y sus agentes. Sin tarifas planas infladas: cada crédito medido en vivo y desglosado en tu panel.</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {[
          ['💎 Consumo real medido en vivo', 'Pagas exactamente lo que usas: tokens, minutos de voz, videos y tareas de agentes.'],
          ['🧠 Todas las IA en un solo idioma', 'Kimi, GPT, Claude, voz, video y automatización orquestados por Aynara: tú solo pides.'],
          ['🧾 Precio final todo incluido', 'Pagas por lo que consumes, sin sorpresas. Desglose línea a línea visible, compra de créditos unificada y facturas PDF automáticas cada mes.'],
        ].map(([t, d]) => (
          <div key={t} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
            <p className="text-sm font-bold text-white">{t}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const SolutionsSection = () => (
  <Section id="soluciones-homepage">
    <div className="max-w-7xl mx-auto">
      <motion.div variants={fadeInUp} className="max-w-3xl mb-golden-xl">
        <p className="editorial-kicker mb-5">Lo que sí resolvemos</p>
        <h2 className="mb-golden-md" data-testid="homepage-solutions-title">
          Neuralia 369 convierte la IA en <span className="gradient-text">infraestructura útil para vender, operar y crecer</span>
        </h2>
        <p className="text-lg text-text-body" data-testid="homepage-solutions-body">
          Lo mejor del mercado es la claridad comercial. Aquí la unimos con nuestra ventaja: contratos, onboarding, conexiones seguras, equipos por rol y una capa conversacional propia.
        </p>
      </motion.div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {SOLUTION_PILLARS.map(({ id, icon: Icon, title, copy }) => (
          <motion.div key={id} variants={fadeInUp} className="glass-card tracing-panel p-6" data-testid={`homepage-solution-card-${id}`}>
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
              <Icon className="w-6 h-6" />
            </div>
            <p className="mt-4 text-lg font-semibold text-text-heading">{title}</p>
            <p className="mt-3 text-sm text-text-body">{copy}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </Section>
);

const ValueVideoSection = () => {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  const toggleMute = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (videoRef.current) {
      videoRef.current.muted = nextMuted;
      if (!nextMuted) {
        videoRef.current.play().catch(() => {});
      }
    }
  };

  return (
    <Section id="video-valor" className="bg-gradient-to-b from-transparent to-primary/5">
      <div className="max-w-7xl mx-auto grid gap-golden-xl xl:grid-cols-[1.08fr_0.92fr] items-start">
        <motion.div variants={fadeInUp} className="glass-card tracing-panel p-4 sm:p-5 lg:p-6" data-testid="homepage-value-video-shell">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <p className="editorial-kicker text-primary/90" data-testid="homepage-value-video-kicker">Vídeo final de valor y soluciones</p>
              <p className="mt-2 text-lg font-semibold text-text-heading" data-testid="homepage-value-video-title">Una pieza centrada en lo que resolvemos y cómo lo convertimos en operación real.</p>
            </div>
            <div className="flex items-center gap-2" data-testid="homepage-value-video-controls">
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-primary">Live preview</span>
              <button type="button" onClick={toggleMute} className="btn-secondary !px-4 !py-3" data-testid="homepage-value-video-mute-button">
                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                {isMuted ? 'Activar audio' : 'Silenciar'}
              </button>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-slate-950/80 aspect-video" data-testid="homepage-value-video-frame">
            <video
              ref={videoRef}
              src={VALUE_VIDEO}
              poster={HERO_VISUAL}
              autoPlay
              loop
              playsInline
              muted={isMuted}
              preload="metadata"
              controls
              className="h-full w-full object-cover"
              data-testid="homepage-value-video-player"
            />
            <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-2 rounded-full border border-white/15 bg-slate-950/60 px-3 py-2 text-xs uppercase tracking-[0.18em] text-white/85">
              <CirclePlay className="w-4 h-4 text-primary" />
              Valor + soluciones Neuralia 369
            </div>
          </div>
        </motion.div>

        <motion.div variants={fadeInUp} className="space-y-4">
          <div className="glass-card tracing-panel p-6" data-testid="homepage-value-video-copy-card">
            <p className="editorial-kicker mb-4">Qué debe dejar claro</p>
            <div className="space-y-3">
              {VALUE_VIDEO_POINTS.map((item, index) => (
                <div key={item} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`homepage-value-video-point-${index}`}>
                  <p className="text-sm text-text-body">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card tracing-panel p-6" data-testid="homepage-value-video-cta-card">
            <p className="text-lg font-semibold text-text-heading">¿Quieres una versión enfocada a tu negocio?</p>
            <p className="mt-3 text-sm text-text-body">Podemos convertir esta misma pieza en una explicación específica para partners, equipos o empresas que necesiten onboarding, automatización y control.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/activar" className="btn-primary" data-testid="homepage-value-video-primary-cta">
                Quiero mi ruta de activación
              </Link>
              <Link to="/media-demos" className="btn-secondary" data-testid="homepage-value-video-secondary-cta">
                Ver más demos
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

const IdentitySection = () => (
  <Section id="que-es-neuralia">
    <div className="max-w-7xl mx-auto grid gap-golden-xl xl:grid-cols-2">
      <motion.div variants={fadeInUp} className="glass-card tracing-panel p-golden-xl" data-testid="homepage-identity-what-we-are">
        <p className="editorial-kicker mb-5">Qué es Neuralia 369</p>
        <h2 className="mb-golden-md">Un sistema operativo con IA para el negocio real</h2>
        <div className="space-y-3">
          {WHAT_WE_ARE.map((item, index) => (
            <div key={item} className="flex items-start gap-3 rounded-[24px] border border-primary/15 bg-primary/5 p-4" data-testid={`homepage-identity-positive-${index}`}>
              <CheckCircle2 className="w-5 h-5 mt-0.5 text-primary shrink-0" />
              <p className="text-sm text-text-body">{item}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div variants={fadeInUp} className="glass-card tracing-panel p-golden-xl" data-testid="homepage-identity-what-we-are-not">
        <p className="editorial-kicker mb-5">Qué NO es</p>
        <h2 className="mb-golden-md">No más dispersión disfrazada de innovación</h2>
        <div className="space-y-3">
          {WHAT_WE_ARE_NOT.map((item, index) => (
            <div key={item} className="flex items-start gap-3 rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid={`homepage-identity-negative-${index}`}>
              <Wrench className="w-5 h-5 mt-0.5 text-text-muted shrink-0" />
              <p className="text-sm text-text-body">{item}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </Section>
);

const ComparisonSection = () => (
  <Section id="comparativa-neuralia-os" className="bg-gradient-to-b from-transparent to-primary/5">
    <div className="max-w-7xl mx-auto">
      <motion.div variants={fadeInUp} className="max-w-3xl mb-golden-xl">
        <p className="editorial-kicker mb-5">Herramientas dispersas vs Neuralia 369</p>
        <h2 className="mb-golden-md" data-testid="homepage-comparison-title">
          La idea fuerte es esta: <span className="gradient-text">no necesitas más apps, necesitas que trabajen integradas</span>
        </h2>
        <p className="text-lg text-text-body" data-testid="homepage-comparison-body">
          Tomamos ese punto fuerte y lo bajamos a nuestro sistema real: ventas, contratos, conexiones, roles, paneles y operación continua.
        </p>
      </motion.div>

      <div className="glass-card tracing-panel overflow-hidden" data-testid="homepage-comparison-table-shell">
        <div className="grid grid-cols-1 border-b border-border-subtle md:grid-cols-[0.8fr_1fr_1fr]">
          <div className="p-5 md:p-6" />
          <div className="p-5 md:p-6 border-t md:border-t-0 md:border-l border-border-subtle bg-background/35" data-testid="homepage-comparison-column-fragmented">
            <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Herramientas dispersas</p>
            <p className="mt-2 text-lg font-semibold text-text-heading">Más piezas, menos control</p>
          </div>
          <div className="p-5 md:p-6 border-t md:border-t-0 md:border-l border-primary/20 bg-primary/10" data-testid="homepage-comparison-column-neuralia">
            <p className="text-xs uppercase tracking-[0.18em] text-primary">Neuralia 369 OS</p>
            <p className="mt-2 text-lg font-semibold text-text-heading">Una capa que coordina todo</p>
          </div>
        </div>

        {COMPARISON_ROWS.map((row, index) => (
          <div key={row.label} className="grid grid-cols-1 md:grid-cols-[0.8fr_1fr_1fr]" data-testid={`homepage-comparison-row-${index}`}>
            <div className="p-5 md:p-6 border-b border-border-subtle bg-background/20">
              <p className="text-sm font-semibold text-text-heading">{row.label}</p>
            </div>
            <div className="p-5 md:p-6 border-b border-border-subtle md:border-l bg-background/40">
              <p className="text-sm text-text-body">{row.fragmented}</p>
            </div>
            <div className="p-5 md:p-6 border-b border-primary/15 md:border-l bg-primary/10/80">
              <p className="text-sm text-text-body">{row.neuralia}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

const ConnectorsSection = () => (
  <Section id="conexiones-embebidas">
    <div className="max-w-7xl mx-auto grid gap-golden-xl xl:grid-cols-[0.92fr_1.08fr] items-start">
      <motion.div variants={fadeInUp} className="space-y-4">
        <p className="editorial-kicker">Conexiones embebidas y autorización</p>
        <h2 data-testid="homepage-connectors-title">Conecta herramientas reales sin pedir contraseñas al cliente</h2>
        <p className="text-lg text-text-body" data-testid="homepage-connectors-body">
          Aquí integramos otro gran acierto de la referencia: mostrar que la plataforma no vive aislada. Neuralia 369 ya tiene la lógica para Auth Links, contratos y un hub de conexiones embebidas que prepara a Aynara para actuar sobre herramientas autorizadas.
        </p>

        <div className="grid gap-4">
          {CONNECTION_FLOW.map(({ id, icon: Icon, title, copy }) => (
            <div key={id} className="glass-card tracing-panel p-5" data-testid={`homepage-connection-flow-${id}`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-text-heading">{title}</p>
                  <p className="mt-2 text-sm text-text-body">{copy}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div variants={fadeInUp} className="glass-card tracing-panel p-golden-xl" data-testid="homepage-connectors-grid-shell">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Hub de conectores</p>
            <p className="mt-2 text-xl font-semibold text-text-heading">Aynara como nodo central</p>
          </div>
          <Link to="/activar" className="btn-secondary" data-testid="homepage-connectors-cta">
            Solicitar activación
          </Link>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {CONNECTORS.map((connector, index) => (
            <div key={connector.name} className="rounded-[24px] border border-border-subtle bg-background/60 p-4 flex items-center gap-4" data-testid={`homepage-connector-card-${index}`}>
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center p-2 shrink-0">
                <img src={connector.logo} alt={connector.name} className="w-full h-full object-contain" data-testid={`homepage-connector-logo-${index}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-text-heading">{connector.name}</p>
                <p className="text-xs text-text-muted">Conexión autorizada</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-[28px] border border-primary/20 bg-primary/10 p-6" data-testid="homepage-connectors-central-card">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-[20px] bg-background flex items-center justify-center text-primary shrink-0">
              <Bot className="w-7 h-7" />
            </div>
            <div>
              <p className="text-lg font-semibold text-text-heading">Aynara recibe contexto y decide acción</p>
              <p className="mt-2 text-sm text-text-body">La diferencia no es solo conectar Gmail o HubSpot. La diferencia es que el sistema sepa cuándo cobrar, cuándo pedir consentimiento, cuándo responder, cuándo delegar y cuándo escalar.</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </Section>
);

const TeamRolesSection = () => {
  const [activeRole, setActiveRole] = useState(TEAM_ROLES[0].id);
  const role = TEAM_ROLES.find((item) => item.id === activeRole) || TEAM_ROLES[0];

  return (
    <Section id="multiroles-clinicas" className="bg-gradient-to-b from-transparent to-primary/5">
      <div className="max-w-7xl mx-auto grid gap-golden-xl xl:grid-cols-[0.84fr_1.16fr] items-start">
        <motion.div variants={fadeInUp}>
          <p className="editorial-kicker mb-5">Caso práctico · Equipos multirrol</p>
          <h2 className="mb-golden-md" data-testid="homepage-clinic-title">Un mismo sistema, varios equipos, cada uno con su contexto real</h2>
          <p className="text-lg text-text-body" data-testid="homepage-clinic-body">
            Esta sección refuerza confianza porque muestra que Neuralia 369 no es solo narrativa. Ya está pensado para organizaciones donde distintas personas necesitan ver cosas distintas sin perder el hilo común.
          </p>
          <div className="mt-golden-lg flex flex-wrap gap-3" data-testid="homepage-clinic-tabs">
            {TEAM_ROLES.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveRole(item.id)}
                className={`rounded-full px-4 py-3 text-sm font-medium transition-colors duration-300 ${activeRole === item.id ? 'bg-primary text-background border border-primary' : 'bg-background/55 text-text-body border border-border-subtle hover:border-primary/35 hover:text-text-heading'}`}
                data-testid={`homepage-clinic-tab-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div variants={fadeInUp} className="glass-card tracing-panel p-golden-xl" data-testid="homepage-clinic-preview-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Rol activo</p>
              <h3 className="mt-2 text-2xl font-semibold text-text-heading" data-testid="homepage-clinic-preview-title">{role.title}</h3>
            </div>
            <div className="rounded-full border border-primary/25 bg-primary/10 px-4 py-2 text-xs uppercase tracking-[0.18em] text-primary" data-testid="homepage-clinic-preview-role-label">
              {role.label}
            </div>
          </div>

          <p className="mt-4 text-text-body" data-testid="homepage-clinic-preview-copy">{role.copy}</p>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {role.metrics.map((metric, index) => (
              <div key={metric} className="rounded-[24px] border border-border-subtle bg-background/60 p-4" data-testid={`homepage-clinic-metric-${index}`}>
                <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Indicador</p>
                <p className="mt-3 text-sm font-semibold text-text-heading">{metric}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-2">
            {role.bullets.map((bullet, index) => (
              <div key={bullet} className="rounded-[24px] border border-primary/10 bg-primary/5 p-4 flex items-start gap-3" data-testid={`homepage-clinic-bullet-${index}`}>
                <BadgeCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-text-body">{bullet}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

const ProofSection = () => (
  <Section id="prueba-social-operativa">
    <div className="max-w-7xl mx-auto grid gap-golden-xl xl:grid-cols-[0.9fr_1.1fr] items-start">
      <motion.div variants={fadeInUp}>
        <p className="editorial-kicker mb-5">Confianza comercial</p>
        <h2 className="mb-golden-md" data-testid="homepage-proof-title">No solo contamos una visión. Ya hay piezas reales funcionando.</h2>
        <p className="text-lg text-text-body" data-testid="homepage-proof-body">
          Como todavía no toca inventar testimonios, lo fuerte aquí es enseñar señales verificables: venta real, contrato, conexión, onboarding partner y estructura multi-equipo lista para escalar.
        </p>
        <div className="mt-golden-lg rounded-[32px] border border-primary/20 bg-primary/10 p-6" data-testid="homepage-proof-live-card">
          <p className="text-xs uppercase tracking-[0.18em] text-primary">Estado LIVE</p>
          <p className="mt-3 text-xl font-semibold text-text-heading">Stripe, Gmail, contratos y paneles ya forman parte del mismo sistema.</p>
          <p className="mt-3 text-sm text-text-body">Esto reduce objeciones porque la web ya no suena a promesa abstracta: suena a operación que se puede activar.</p>
        </div>
        <div className="mt-4 rounded-[32px] border border-border-subtle bg-background/55 p-6" data-testid="homepage-proof-strategy-card">
          <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Por qué convierte mejor ahora</p>
          <p className="mt-3 text-lg font-semibold text-text-heading">Juntamos todo lo que el cliente necesita ver para confiar.</p>
          <p className="mt-3 text-sm text-text-body">Oferta clara, operación real, rutas de activación, señales LIVE y una narrativa que baja la IA del discurso a la ejecución.</p>
        </div>
      </motion.div>

      <motion.div variants={fadeInUp} className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          {LIVE_PROOF.map((item) => (
            <div key={item.id} className="glass-card tracing-panel p-6" data-testid={`homepage-proof-card-${item.id}`}>
              <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Señal de confianza</p>
              <p className="mt-3 text-lg font-semibold text-text-heading">{item.title}</p>
              <p className="mt-3 text-sm text-text-body">{item.copy}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2" data-testid="homepage-real-cases-grid">
          {REAL_CASES.map((item) => (
            <div key={item.id} className="glass-card tracing-panel p-6" data-testid={`homepage-real-case-${item.id}`}>
              <p className="text-xs uppercase tracking-[0.18em] text-primary">{item.label}</p>
              <p className="mt-3 text-lg font-semibold text-text-heading">{item.title}</p>
              <p className="mt-3 text-sm text-text-body">{item.copy}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span key={tag} className="rounded-full border border-primary/20 bg-primary/10 px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-primary" data-testid={`homepage-real-case-tag-${item.id}-${tag}`}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </Section>
);

const ProcessSection = () => (
  <Section id="proceso-neuralia" className="bg-gradient-to-b from-transparent to-primary/5">
    <div className="max-w-7xl mx-auto">
      <motion.div variants={fadeInUp} className="max-w-3xl mb-golden-xl">
        <p className="editorial-kicker mb-5">De la idea a la activación</p>
        <h2 className="mb-golden-md" data-testid="homepage-process-title">Una ruta comercial y operativa mucho más clara</h2>
        <p className="text-lg text-text-body" data-testid="homepage-process-body">
          Otro punto fuerte que integramos de la referencia es enseñar el proceso. Cuando un cliente entiende el camino, compra con menos fricción.
        </p>
      </motion.div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {PROCESS_STEPS.map((step, index) => (
          <motion.div key={step.id} variants={fadeInUp} className="glass-card tracing-panel p-6" data-testid={`homepage-process-step-${index}`}>
            <p className="text-xs uppercase tracking-[0.18em] text-primary">Fase {index + 1}</p>
            <p className="mt-3 text-lg font-semibold text-text-heading">{step.title}</p>
            <p className="mt-3 text-sm text-text-body">{step.copy}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </Section>
);

const ResultsIn7DaysSection = () => (
  <Section id="resultados-7-dias" className="bg-gradient-to-b from-transparent to-primary/5">
    <div className="max-w-7xl mx-auto grid gap-golden-xl xl:grid-cols-[1.04fr_0.96fr] items-start">
      <motion.div variants={fadeInUp}>
        <p className="editorial-kicker mb-5" data-testid="homepage-roi-kicker">Resultados en 7 días</p>
        <h2 className="mb-golden-md" data-testid="homepage-roi-title">
          Si quieres cerrar mejor, la promesa no puede ser humo. <span className="gradient-text">Tiene que sonar a avance real en una semana.</span>
        </h2>
        <p className="text-lg text-text-body" data-testid="homepage-roi-body">
          Este bloque está pensado para vender con más fuerza: en los primeros 7 días no prometemos magia, prometemos orden, primeras conexiones y un flujo comercial mucho más cerca de funcionar como sistema.
        </p>

        <div className="mt-golden-lg grid gap-4" data-testid="homepage-roi-grid">
          {ROI_OUTCOMES.map((item, index) => (
            <div key={item.id} className="glass-card tracing-panel p-6" data-testid={`homepage-roi-card-${item.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs uppercase tracking-[0.18em] text-primary" data-testid={`homepage-roi-card-day-${item.id}`}>{item.day}</p>
                <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-primary" data-testid={`homepage-roi-card-pill-${item.id}`}>
                  Resultado {index + 1}
                </span>
              </div>
              <p className="mt-3 text-xl font-semibold text-text-heading" data-testid={`homepage-roi-card-title-${item.id}`}>{item.title}</p>
              <p className="mt-3 text-sm text-text-body" data-testid={`homepage-roi-card-copy-${item.id}`}>{item.copy}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div variants={fadeInUp} className="space-y-4">
        <div className="glass-card tracing-panel p-golden-xl" data-testid="homepage-roi-sales-card">
          <p className="text-xs uppercase tracking-[0.18em] text-primary">Cierre más agresivo</p>
          <p className="mt-3 text-2xl font-semibold text-text-heading">No compres una promesa vaga. Compra una semana de avance visible.</p>
          <p className="mt-3 text-sm text-text-body">Ese es el giro comercial: pasar de “algún día automatizaremos esto” a “esta misma semana dejamos la base activa para vender y operar mejor”.</p>
          <div className="mt-5 flex flex-wrap gap-3" data-testid="homepage-roi-cta-group">
            <Link to="/activar" className="btn-primary" data-testid="homepage-roi-primary-cta">
              Quiero activar mi semana 1
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/agenda" className="btn-secondary" data-testid="homepage-roi-secondary-cta">
              Prefiero revisar la ruta
            </Link>
          </div>
        </div>

        <div className="glass-card tracing-panel p-golden-xl" data-testid="homepage-pending-connections-card">
          <div className="flex items-center gap-3">
            <Network className="w-5 h-5 text-primary" />
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Próximas activaciones</p>
              <p className="mt-2 text-xl font-semibold text-text-heading">Conexiones que siguen en cola de prioridad</p>
            </div>
          </div>
          <div className="mt-5 space-y-3" data-testid="homepage-pending-connections-list">
            {PENDING_CONNECTIONS.map((item) => (
              <div key={item.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`homepage-pending-connection-${item.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-text-heading" data-testid={`homepage-pending-connection-title-${item.id}`}>{item.title}</p>
                  <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-primary" data-testid={`homepage-pending-connection-priority-${item.id}`}>{item.priority}</span>
                </div>
                <p className="mt-2 text-sm text-text-body" data-testid={`homepage-pending-connection-copy-${item.id}`}>{item.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </Section>
);

const TESTIMONIALS = [
  {
    id: "sandra",
    name: "Dra. Sandra Chávez",
    role: "Directora · Clínica Dental Sonríe Perú",
    country: "Lima, Perú",
    flag: "🇵🇪",
    avatar_initials: "SC",
    avatar_color: "from-cyan-500 to-teal-400",
    quote: "En 3 semanas Aynara dejó de ser un experimento y se convirtió en nuestra recepcionista de guardia. Responde consultas a las 2 AM, califica a los pacientes y ya agenda citas sin que yo intervenga. El primer mes recuperamos la inversión.",
    metrics: [
      { label: "Citas agendadas / mes", value: "+47", detail: "antes: 0 sin secretaria" },
      { label: "Tiempo de respuesta WA", value: "< 90 seg", detail: "24 h al día" },
      { label: "ROI primer mes", value: "3.2×", detail: "sobre la licencia" },
    ],
    verified: true,
    date: "Oct 2026",
  },
  {
    id: "carlos-pr",
    name: "Carlos Rodríguez",
    role: "CEO · Grupo Caribe Consultoría",
    country: "San Juan, Puerto Rico",
    flag: "🇵🇷",
    avatar_initials: "CR",
    avatar_color: "from-violet-500 to-purple-400",
    quote: "Nuestro equipo de ventas tiraba leads sin seguimiento. Con el Agente CRM de Neuralia 369 automatizamos el pipeline completo: scoring, WhatsApp y cierre. Cerramos $18,000 en contratos en el primer mes sin contratar a nadie más.",
    metrics: [
      { label: "Contratos cerrados", value: "$18 K", detail: "primer mes" },
      { label: "Leads calificados auto", value: "83 %", detail: "sin intervención manual" },
      { label: "Equipo adicional", value: "0 personas", detail: "mismo equipo, más volumen" },
    ],
    verified: false,
    date: "Sep 2026",
  },
  {
    id: "valeria-pe",
    name: "Valeria Quispe",
    role: "Fundadora · Tienda Moda Andina",
    country: "Arequipa, Perú",
    flag: "🇵🇪",
    avatar_initials: "VQ",
    avatar_color: "from-rose-500 to-pink-400",
    quote: "Tenía miedo de que la IA sonara robótica con mis clientas. Pero Aynara habla como una asesora de verdad: recuerda tallas, pregunta si llegó bien el pedido y manda la factura sola. Mis ventas por WA subieron un 61 % en 6 semanas.",
    metrics: [
      { label: "Ventas por WhatsApp", value: "+61 %", detail: "en 6 semanas" },
      { label: "Tiempo en atención", value: "−4 h/día", detail: "liberadas para diseño" },
      { label: "Reseñas 5 estrellas", value: "×2", detail: "atención post-venta automática" },
    ],
    verified: false,
    date: "Oct 2026",
  },
];

// ── RECURSOS EXTRACTO ────────────────────────────────────────────────────────
const BLOG_POSTS = [
  {
    slug: 'sistema-multiagente-empresas',
    tag: 'Agentes IA',
    title: 'Por qué el sistema multiagente supera al chatbot único en operaciones reales',
    excerpt: 'Un solo agente generalista collapsa ante cargas mixtas. Los sistemas multiagente dividen la responsabilidad y escalan sin fricción.',
    time: '5 min',
    color: 'from-cyan-500/10 to-blue-500/5 border-cyan-500/15',
    dot: 'bg-cyan-400',
  },
  {
    slug: 'seguridad-agentes-ia',
    tag: 'Seguridad',
    title: 'Auth Links y permisos granulares: el estándar de seguridad para agentes IA en 2026',
    excerpt: 'Cómo los Auth Links con expiración reemplazan a los tokens estáticos y reducen la superficie de ataque de un agente de IA en producción.',
    time: '7 min',
    color: 'from-violet-500/10 to-purple-500/5 border-violet-500/15',
    dot: 'bg-violet-400',
  },
  {
    slug: 'deepseek-vs-gpt-empresas',
    tag: 'Modelos',
    title: 'DeepSeek R1 vs GPT-4o para empresas: cuándo elegir cada uno',
    excerpt: 'Benchmark práctico de coste por token, latencia y precisión en tareas de clasificación y extracción de datos empresariales.',
    time: '6 min',
    color: 'from-amber-500/10 to-yellow-500/5 border-amber-500/15',
    dot: 'bg-amber-400',
  },
];

const RecursosExtractSection = () => (
  <Section id="recursos-extracto" data-testid="homepage-recursos-section">
    <motion.div variants={fadeInUp} className="flex items-end justify-between mb-golden-xl flex-wrap gap-4">
      <div>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-golden-md">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          <span className="text-sm text-amber-400 font-semibold tracking-wide uppercase">Recursos</span>
        </div>
        <h2>Del laboratorio al <span className="gradient-text">negocio</span></h2>
        <p className="text-text-body text-lg mt-2 max-w-lg">Artículos técnicos escritos por el equipo de Neuralia 369 — sin patrocinios, sin sesgo.</p>
      </div>
      <Link to="/recursos" className="btn-secondary text-sm whitespace-nowrap" data-testid="recursos-see-all-btn">
        Ver todos los artículos →
      </Link>
    </motion.div>
    <div className="grid md:grid-cols-3 gap-5" data-testid="recursos-grid">
      {BLOG_POSTS.map((post, i) => (
        <motion.div key={post.slug} variants={fadeInUp} custom={i}>
          <Link
            to={`/recursos/${post.slug}`}
            className={`group flex flex-col h-full rounded-2xl border bg-gradient-to-br ${post.color} p-6 hover:brightness-110 transition-all duration-200`}
            data-testid={`blog-card-${post.slug}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-1.5 h-1.5 rounded-full ${post.dot}`} />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{post.tag}</span>
              <span className="ml-auto text-[10px] text-slate-600">{post.time} de lectura</span>
            </div>
            <h3 className="text-base font-bold text-white leading-snug mb-3 group-hover:text-primary transition-colors flex-1">{post.title}</h3>
            <p className="text-xs text-slate-400 leading-relaxed">{post.excerpt}</p>
            <div className="mt-4 flex items-center gap-1 text-xs text-primary font-semibold">
              Leer artículo <span className="group-hover:translate-x-1 transition-transform inline-block">→</span>
            </div>
          </Link>
        </motion.div>
      ))}
    </div>
  </Section>
);

// ── PRÓXIMAMENTE ─────────────────────────────────────────────────────────────
const ProximamenteSection = () => (
  <Section id="proximamente" data-testid="homepage-proximamente-section">
    <motion.div variants={fadeInUp}
      className="relative overflow-hidden rounded-3xl border border-white/[0.07] bg-gradient-to-br from-[#0d1f3c] via-[#081428] to-[#0a1628] p-10 md:p-14"
    >
      {/* Background glow */}
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-secondary/5 blur-3xl pointer-events-none" />

      <div className="relative grid md:grid-cols-2 gap-12 items-center">
        {/* App Móvil */}
        <motion.div variants={fadeInUp} className="space-y-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-emerald-400 font-bold uppercase tracking-widest">En diseño · 2027</span>
          </div>
          <h2 className="text-3xl font-black text-white leading-tight">
            App Móvil <span className="gradient-text">Aynara</span>
          </h2>
          <p className="text-slate-400 leading-relaxed">
            Control completo del ecosistema de agentes desde tu móvil. Aprueba leads, revisa alertas de seguridad y recibe resúmenes del día — sin abrir un portátil.
          </p>
          <ul className="space-y-2">
            {['Dashboard de agentes en tiempo real','Notificaciones push por acción crítica','Aprobar y rechazar leads con un toque','Resumen diario automático a las 8 PM'].map(f => (
              <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          <Link to="/app-movil" className="btn-primary inline-flex" data-testid="proximamente-app-movil-btn">
            Unirse a la lista de espera
          </Link>
        </motion.div>

        {/* Tendencias */}
        <motion.div variants={fadeInUp} custom={1} className="space-y-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/20">
            <span className="text-xs text-rose-400 font-bold uppercase tracking-widest">Hoja de ruta 2026 – 2027</span>
          </div>
          <h2 className="text-3xl font-black text-white leading-tight">
            Tendencias <span className="gradient-text">verificadas</span>
          </h2>
          <p className="text-slate-400 leading-relaxed">
            6 hitos en la hoja de ruta de Neuralia 369 con estado actualizado en tiempo real. Datos de Gartner, Microsoft y Verizon — sin especulación.
          </p>
          <ul className="space-y-2">
            {['Gartner: 70% de org. usarán IA multi-agente en 2028','Microsoft: 82% de líderes planean aumentar inversión en IA','MCP como nuevo estándar de conectividad de agentes','Dashboard de Tendencias con actualizaciones trimestrales'].map(f => (
              <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                <div className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          <Link to="/tendencias" className="btn-secondary inline-flex" data-testid="proximamente-tendencias-btn">
            Ver hoja de ruta →
          </Link>
        </motion.div>
      </div>
    </motion.div>
  </Section>
);

const TestimonialsSection = () => (
  <Section id="casos-exito" data-testid="homepage-testimonials-section">
    <motion.div variants={fadeInUp} className="text-center mb-golden-xl">
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-golden-md" data-testid="testimonials-badge">
        <Star className="w-4 h-4 text-primary fill-primary" />
        <span className="text-sm text-primary font-semibold tracking-wide uppercase">Casos de Éxito</span>
      </div>
      <h2 className="mb-golden-sm" data-testid="testimonials-title">
        Resultados que <span className="gradient-text">ya están ocurriendo</span>
      </h2>
      <p className="text-text-body text-lg max-w-2xl mx-auto" data-testid="testimonials-subtitle">
        Clientes reales. Métricas reales. Sin promesas vacías.
      </p>
    </motion.div>

    <div className="grid md:grid-cols-3 gap-6" data-testid="testimonials-grid">
      {TESTIMONIALS.map((t, i) => (
        <motion.div
          key={t.id}
          variants={fadeInUp}
          custom={i}
          className="glass-card p-7 flex flex-col gap-5 relative overflow-hidden group hover:border-primary/30 transition-colors duration-300"
          data-testid={`testimonial-card-${t.id}`}
        >
          {/* Decorative glow */}
          <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-primary/5 blur-2xl group-hover:bg-primary/10 transition-colors duration-500 pointer-events-none" />

          {/* Avatar + nombre */}
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${t.avatar_color} flex items-center justify-center text-white font-black text-sm shrink-0`}>
              {t.avatar_initials}
            </div>
            <div className="min-w-0">
              <p className="font-bold text-white text-sm leading-tight truncate">{t.name}</p>
              <p className="text-xs text-slate-400 leading-tight mt-0.5 truncate">{t.role}</p>
              <p className="text-xs text-slate-500 mt-0.5">{t.flag} {t.country}</p>
            </div>
            {t.verified && (
              <div className="ml-auto shrink-0" title="Cliente verificado">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              </div>
            )}
          </div>

          {/* Quote */}
          <blockquote className="text-sm text-slate-300 leading-relaxed italic border-l-2 border-primary/40 pl-4 flex-1">
            "{t.quote}"
          </blockquote>

          {/* Métricas */}
          <div className="grid grid-cols-3 gap-2 pt-4 border-t border-white/[0.06]">
            {t.metrics.map((m) => (
              <div key={m.label} className="text-center">
                <p className="text-base font-black text-primary leading-none">{m.value}</p>
                <p className="text-[10px] text-slate-500 mt-1 leading-tight">{m.detail}</p>
              </div>
            ))}
          </div>

          {/* Fecha + badge */}
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-slate-600">{t.date}</span>
            {!t.verified && <span className="text-[10px] text-slate-600 italic">caso ilustrativo</span>}
          </div>
        </motion.div>
      ))}
    </div>

    {/* CTA sutil */}
    <motion.div variants={fadeInUp} className="text-center mt-golden-xl" data-testid="testimonials-cta">
      <p className="text-text-body mb-golden-md">¿Quieres ser el próximo caso de éxito?</p>
      <Link to="/activar" className="btn-primary" data-testid="testimonials-cta-btn">
        Solicitar activación
      </Link>
    </motion.div>
  </Section>
);

const ContactSection = () => {
  const [formData, setFormData] = useState({ nombre: '', email: '', empresa: '', country: '', mensaje: '', source: 'homepage_contact_form' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (data.success) {
        trackLeadEvent({ source: 'homepage_contact_form' });
        setSubmitted(true);
        setFormData({ nombre: '', email: '', empresa: '', country: '', mensaje: '', source: 'homepage_contact_form' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Section id="contacto" className="bg-gradient-to-t from-primary/5 to-transparent">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-golden-2xl text-center" data-testid="homepage-contact-success-state">
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-golden-md" />
            <h2 className="mb-golden-md">Solicitud enviada a <span className="gradient-text">Aynara</span></h2>
            <p className="text-xl text-text-body mb-golden-lg max-w-lg mx-auto">Te responderemos con la mejor ruta: contrato, Auth Link, onboarding o activación comercial según tu caso.</p>
          </motion.div>
        </div>
      </Section>
    );
  }

  return (
    <Section id="contacto" className="bg-gradient-to-t from-primary/5 to-transparent">
      <div className="max-w-5xl mx-auto">
        <motion.div variants={fadeInUp} className="text-center mb-golden-xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-golden-md" data-testid="homepage-contact-badge">
            <MessageSquare className="w-4 h-4 text-accent" />
            <span className="text-sm text-accent font-medium">Activación Neuralia 369</span>
          </div>
          <h2 className="mb-golden-md" data-testid="homepage-contact-title">Cuéntanos qué quieres vender, conectar o automatizar y <span className="gradient-text">preparamos la ruta exacta</span></h2>
          <p className="text-lg text-text-body max-w-3xl mx-auto" data-testid="homepage-contact-body">Si ya tienes claro el problema, mejor. Si no, también. La web ahora está pensada para que conversemos con una estructura real de activación.</p>
        </motion.div>

        <motion.form variants={fadeInUp} onSubmit={handleSubmit} className="glass-card p-golden-xl relative overflow-hidden" data-testid="homepage-contact-form">
          <div className="grid md:grid-cols-2 gap-golden-lg mb-golden-lg">
            <input type="text" className="input-kinesthetic" placeholder="Tu nombre" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} required data-testid="homepage-contact-input-name" />
            <input type="email" className="input-kinesthetic" placeholder="tu@email.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="homepage-contact-input-email" />
          </div>
          <div className="grid md:grid-cols-2 gap-golden-lg mb-golden-lg">
            <input type="text" className="input-kinesthetic" placeholder="Nombre de tu empresa" value={formData.empresa} onChange={(e) => setFormData({ ...formData, empresa: e.target.value })} data-testid="homepage-contact-input-company" />
            <input type="text" className="input-kinesthetic" placeholder="País" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} data-testid="homepage-contact-input-country" />
          </div>
          <textarea className="input-kinesthetic min-h-[160px] resize-none mb-golden-xl" placeholder="Quiero una web más convincente, onboarding real, contrato + Auth Link, conexiones seguras, paneles por rol, automatización comercial o activación para equipos/partners..." value={formData.mensaje} onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })} required data-testid="homepage-contact-input-message" />
          <button type="submit" className="btn-primary w-full sm:w-auto justify-center min-w-[240px]" disabled={isSubmitting} data-testid="homepage-contact-submit-button">
            {isSubmitting ? 'Enviando...' : <><Send className="w-5 h-5" /> Solicitar activación con Aynara</>}
          </button>
          <Link to="/activar" className="btn-secondary mt-4 w-full sm:w-auto justify-center" data-testid="homepage-contact-direct-route-link">
            Ir a ruta directa de contrato/Auth Link
          </Link>
        </motion.form>
      </div>
    </Section>
  );
};

const ClosingSection = () => (
  <Section className="bg-gradient-to-b from-transparent to-primary/5">
    <div className="max-w-5xl mx-auto">
      <motion.div variants={fadeInUp} className="glass-card tracing-panel p-golden-2xl text-center" data-testid="homepage-closing-card">
        <p className="editorial-kicker mb-5">Cierre comercial</p>
        <h2 className="mb-golden-md" data-testid="homepage-closing-title">Tu web ya no tiene que sonar a promesa. <span className="gradient-text">Puede sonar a sistema listo para activarse.</span></h2>
        <p className="text-text-body text-lg max-w-3xl mx-auto" data-testid="homepage-closing-body">Eso es exactamente lo que hemos tomado de la referencia: claridad, estructura y confianza. Y lo estamos uniendo a lo que ya hace fuerte a Neuralia 369: Aynara, Auth Links, contratos, conexión segura, partners y equipos por rol.</p>
        <div className="mt-golden-xl flex flex-col sm:flex-row items-center justify-center gap-golden-md" data-testid="homepage-closing-cta-group">
          <Link to="/soluciones" className="btn-secondary" data-testid="homepage-closing-solutions-link">Ver soluciones</Link>
          <Link to="/precios" className="btn-secondary" data-testid="homepage-closing-pricing-link">Ver precios</Link>
          <Link to="/activar" className="btn-primary" data-testid="homepage-closing-agenda-link">Solicitar activación directa</Link>
        </div>
      </motion.div>
    </div>
  </Section>
);

const SocialSection = () => (
  <Section>
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="text-2xl mb-golden-md" data-testid="homepage-social-title">
        Sigue la evolución de Neuralia{' '}<span className="bg-[linear-gradient(90deg,#4285F4_0%,#EA4335_33%,#FBBC05_66%,#34A853_100%)] bg-clip-text text-transparent">369</span>
      </h2>
      <div className="flex flex-wrap items-center justify-center gap-golden-lg mt-golden-lg" data-testid="homepage-social-links">
        <a href={SOCIAL_URLS.facebook} target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="homepage-social-facebook">Facebook</a>
        <a href={SOCIAL_URLS.youtube} target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="homepage-social-youtube">YouTube</a>
        <a href={SOCIAL_URLS.tiktok} target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="homepage-social-tiktok">TikTok</a>
      </div>
    </div>
  </Section>
);

export default function HomePage() {
  return (
    <>
      <SEO
        title="Neuralia369 — La inteligencia que piensa contigo"
        description="La única plataforma de IA con facturación justa y real: pagas por lo que consumes. Contratos, Auth Links, conexiones embebidas, checkout, paneles y automatización real orquestados por Aynara."
        path="/"
      />
      <HeroSection />
      <LiveSignalsBar />
      <ExtractMultiagente />
      <ExtractSeguridad />
      <ExtractSoluciones />
      <UspBanner />
      <SolutionsSection />
      <ValueVideoSection />
      <IdentitySection />
      <ComparisonSection />
      <ConnectorsSection />
      <TeamRolesSection />
      <ProofSection />
      <ProcessSection />
      <ResultsIn7DaysSection />
      <TestimonialsSection />
      <RecursosExtractSection />
      <ProximamenteSection />
      <ContactSection />
      <ClosingSection />
      <Newsletter />
      <SocialSection />
    </>
  );
}